<template>
  <b-overlay
    :show="loadingUpload"
    rounded="sm"
    opacity="0.6"
  >
    <div
      class="m-form-image"
      :class="{ 'm-form-image--error' : state === false, 'is-full' : widthFull && imageUrl, 'is-dragover': isDragOver }"
      @click="selectFile"
      @dragover.prevent="onDragOver"
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDropFile"
    >
      <span class="m-form-image__wrapper">
        <input
          ref="formImage"
          type="file"
          :accept="acceptType.toString()"
          :multiple="multiple"
          @input="onFileSelected"
        >
        <img
          v-if="imageUrl"
          :src="imageUrl"
          alt="icon"
          class="m-form-image__preview"
        >
        <div
          v-else
          class="m-form-image__placeholder"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24px"
            viewBox="0 -960 960 960"
            width="24px"
            class="fill-current"
          ><path d="M480-480ZM212.31-140Q182-140 161-161q-21-21-21-51.31v-535.38Q140-778 161-799q21-21 51.31-21h300v60h-300q-5.39 0-8.85 3.46t-3.46 8.85v535.38q0 5.39 3.46 8.85t8.85 3.46h535.38q5.39 0 8.85-3.46t3.46-8.85v-300h60v300Q820-182 799-161q-21 21-51.31 21H212.31Zm43.08-152.31h449.22L565-478.46 445-322.69l-85-108.08-104.61 138.46ZM680-600v-80h-80v-60h80v-80h60v80h80v60h-80v80h-60Z" /></svg>
          <div class="s-12 mt-25">{{ label }}</div>
        </div>

        <div
          v-if="imageUrl && withLabelName"
          class="m-form-image__labelName"
        >
          {{ labelName }}
        </div>
      </span>
    </div>

    <b-modal
      id="modal-cropper"
      v-model="showModal"
      :no-close-on-backdrop="widthFull"
      :title="fileType.includes('video') ? 'Preview Video' : 'Edit Gambar'"
      @ok="onSubmit"
    >
      <div
        v-if="fileType.includes('image')"
        class="cropper-wrapper"
      >
        <vue-cropper
          ref="cropper"
          drag-mode="move"
          :src="selectedImage"
          :view-mode="2"
          :aspect-ratio="widthFull ? aspectRatio ? parseInt(aspectRatio) : 0 : 1"
          alt="Thumb Image"
          :crop-box-movable="true"
          :crop-box-resizable="widthFull ? true : false"
          :auto-crop-area="1"
          :scalable="widthFull ? true : false"
          :img-style="widthFull ? { width: '100%', height: '100%' } : { width: '400px', height: '300px' }"
        />
      </div>

      <div v-if="fileType.includes('video')">
        <video
          v-show="videoUrl"
          id="video-preview"
          controls
          class="video-preview"
          width="100%"
          height="500px"
        >
          <source
            :src="videoUrl"
            type="video/mp4"
          >
        </video>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="secondary"
          class="float-right"
          @click="cancel()"
        >
          Batal
        </b-button>

        <b-button
          variant="primary"
          class="float-right"
          :disabled="loadingUpload"
          @click="ok()"
        >
          <b-spinner
            v-show="loadingUpload"
            label="Loading..."
            class="mr-1"
            small
          />
          <span>Simpan</span>
        </b-button>
      </template>
    </b-modal>

  </b-overlay>
</template>

<script>
import { BSpinner, BButton, BOverlay } from 'bootstrap-vue'
import { mapActions } from 'vuex'
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'

export default {
  components: {
    VueCropper, BSpinner, BButton, BOverlay,
  },
  props: {
    label: {
      type: String,
      default: 'Upload',
    },
    index: {
      type: Number,
      default: null,
    },
    state: {
      type: Boolean,
      default: null,
    },
    value: {
      type: String,
      default: '',
    },
    width: {
      type: Number,
      default: 120,
    },
    widthFull: {
      type: Boolean,
      default: false,
    },
    withLabelName: {
      type: Boolean,
      default: false,
    },
    labelName: {
      type: String,
      default: '',
    },
    withEndpoin: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    acceptType: {
      type: Array,
      default: () => (['image']),
    },
    aspectRatio: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      imageUrl: this.value,
      videoUrl: this.value,
      selectedImage: '',
      selectedVideo: null,
      showModal: false,
      loadingUpload: false,
      fileType: '',
      isDragOver: false,
    }
  },
  watch: {
    value(value) {
      this.imageUrl = value
    },
    showModal(value) {
      if (!value) {
        this.selectedImage = ''
      }
    },
  },
  methods: {
    ...mapActions('media', ['uploadMedia', 'uploadVideo']),
    selectFile() {
      this.$refs.formImage.click()
    },
    // Add this method to handle the auto-cropping
    async processImageForUpload(file, dataUrl) {
      // Check if file is already 1:1 aspect ratio
      const isAlreadyProcessed = await this.checkImageFormat(file, dataUrl)
      if (isAlreadyProcessed) {
        return { file, dataUrl, type: file.type }
      }

      // Create a new promise to handle image processing
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          // Create canvas for cropping
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          // Calculate dimensions for 1:1 aspect ratio (centered crop)
          const size = Math.min(img.width, img.height)
          const offsetX = (img.width - size) / 2
          const offsetY = (img.height - size) / 2

          // Set canvas to square dimensions
          canvas.width = size
          canvas.height = size

          // Draw the cropped image
          ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size)

          // Convert to original format
          const newDataUrl = canvas.toDataURL(file.type)

          // Convert dataURL to Blob
          canvas.toBlob(blob => {
            // Create a new file from the blob
            const processedFile = new File([blob], file.name, {
              type: file.type,
            })

            resolve({
              file: processedFile,
              dataUrl: newDataUrl,
              type: file.type,
              originalFile: file,
            })
          }, file.type)
        }

        img.onerror = () => {
          reject(new Error('Failed to load image'))
        }

        img.src = dataUrl
      })
    },

    // Method to check if the image already meets our requirements
    async checkImageFormat(file, dataUrl) {
      if (!file.type.includes('image')) {
        return true // Not an image, skip processing
      }

      // Check aspect ratio for all image types
      return new Promise(resolve => {
        const img = new Image()
        img.onload = () => {
          // If the aspect ratio is already 1:1 (with small tolerance)
          const aspectRatio = img.width / img.height
          resolve(Math.abs(aspectRatio - 1) < 0.01)
        }
        img.onerror = () => resolve(false)
        img.src = dataUrl
      })
    },
    onDragOver(e) {
      // eslint-disable-next-line no-param-reassign
      e.dataTransfer.dropEffect = 'copy'
      this.isDragOver = true
    },
    onDragEnter() {
      this.isDragOver = true
    },
    onDragLeave(e) {
      if (!this.$el.contains(e.relatedTarget)) {
        this.isDragOver = false
      }
    },
    onDropFile(e) {
      this.isDragOver = false
      if (!e.dataTransfer) return
      this.extractDroppedFiles(e.dataTransfer).then(files => {
        if (files && files.length) {
          this.processIncomingFiles(files)
        } else {
          this.$store.commit('response/SET_ERROR', {
            message: 'Gagal mengambil gambar yang ditarik. Silakan download gambar terlebih dahulu atau drag dari file di komputer Anda.',
          })
        }
      })
    },
    async extractDroppedFiles(dataTransfer) {
      if (dataTransfer.files && dataTransfer.files.length) {
        return Array.from(dataTransfer.files)
      }

      // Check if URL or HTML was dragged from another website/tab
      let url = dataTransfer.getData('text/uri-list') || dataTransfer.getData('text/plain')
      const html = dataTransfer.getData('text/html')

      if (!url && html) {
        const match = html.match(/<img[^>]+src=["']([^"']+)["']/i)
        if (match) {
          const [, extractedUrl] = match
          url = extractedUrl
        }
      }

      if (url && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:image/'))) {
        try {
          this.loadingUpload = true
          // Try direct fetch first
          const res = await fetch(url)
          const blob = await res.blob()
          const mimeType = blob.type || 'image/jpeg'
          const [, subtype] = mimeType.split('/')
          const extension = subtype || 'jpg'
          const file = new File([blob], `image-${Date.now()}.${extension}`, { type: mimeType })
          return [file]
        } catch (err) {
          // If direct fetch is blocked by CORS, try canvas loading
          try {
            const blob = await this.urlToBlobViaCanvas(url)
            if (blob) {
              const file = new File([blob], `image-${Date.now()}.jpg`, { type: 'image/jpeg' })
              return [file]
            }
          } catch (canvasErr) {
            // eslint-disable-next-line no-console
            console.error('Failed to load dragged image from URL:', canvasErr)
          }
        } finally {
          this.loadingUpload = false
        }
      }

      return []
    },
    urlToBlobViaCanvas(url) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = img.naturalWidth || img.width
            canvas.height = img.naturalHeight || img.height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0)
            canvas.toBlob(blob => {
              if (blob) {
                resolve(blob)
              } else {
                reject(new Error('Canvas toBlob returned null'))
              }
            }, 'image/jpeg', 0.95)
          } catch (e) {
            reject(e)
          }
        }
        img.onerror = () => reject(new Error('Failed to load image from URL'))
        img.src = url
      })
    },
    onFileSelected(e) {
      if (!e.target.files.length) {
        return
      }
      const files = Array.from(e.target.files)
      this.processIncomingFiles(files, e.target)
    },
    processIncomingFiles(files, inputElement = null) {
      if (typeof FileReader !== 'function') {
        this.$store.commit('response/SET_ERROR', {
          message: 'FileReader API tidak mendukung!',
        })
        return
      }

      const isMultipleFiles = this.multiple && files.length > 1

      if (isMultipleFiles) {
        this.loadingUpload = true

        // First, read all files as data URLs
        const readPromises = files.map(file => this.readAsDataURL(file)
          .then(dataUrl => {
            if (file.type.includes('image')) {
              // Process image files
              return this.processImageForUpload(file, dataUrl)
            }
            // Return non-image files as is
            return { file, dataUrl, type: file.type }
          }))

        Promise.all(readPromises)
          .then(filesData => {
            // Then process all files with separate promises
            const processPromises = filesData.map(fileData => {
              if (fileData.type.includes('image')) {
                return this.processFileWithoutEmit(fileData.file, fileData.dataUrl, false)
              } if (fileData.type.includes('video')) {
                return this.processFileWithoutEmit(fileData.file, fileData.dataUrl, true)
              }
              return Promise.resolve(null)
            })

            return Promise.all(processPromises).then(results => {
              // Filter out any null results and pair with the original data URLs
              const validResults = results.filter((result, index) => result && filesData[index])

              // Now emit each result individually
              validResults.forEach(result => {
                const fileData = filesData.find(f => f.file === result.originalFile
                  || (result.data && f.file.name === result.originalFile.name)
                  || (f.originalFile && f.originalFile.name === result.originalFile.name))

                if (fileData) {
                  this.$emit('finish', result.data || fileData.file, this.index)
                  this.$emit('input', fileData.dataUrl)
                }
              })

              return results
            })
          })
          .catch(error => {
            this.$store.commit('response/SET_ERROR', {
              message: `Error processing files: ${error.message}`,
            })
          })
          .finally(() => {
            this.loadingUpload = false
            if (inputElement) {
              // eslint-disable-next-line no-param-reassign
              inputElement.value = ''
            }
          })
      } else {
        const [file] = files
        const url = this.readAsDataURL(file)
        this.fileType = file.type

        url.then(async res => {
          if (this.fileType.includes('image')) {
            // Check if already 1:1, if yes, skip cropper modal
            const is1to1 = await this.checkImageFormat(file, res)
            if (is1to1) {
              this.loadingUpload = true
              this.imageUrl = res
              if (this.withEndpoin) {
                const response = await this.uploadMedia(file)
                if (response) {
                  this.$emit('finish', response.data, this.index)
                  this.$emit('input', res)
                }
              } else {
                this.$emit('finish', file, this.index)
                this.$emit('input', res)
              }
              this.loadingUpload = false
              if (inputElement) {
                // eslint-disable-next-line no-param-reassign
                inputElement.value = ''
              }
              return
            }

            this.selectedImage = res
            if (inputElement) {
              // eslint-disable-next-line no-param-reassign
              inputElement.value = ''
            }
          }

          if (this.fileType.includes('video')) {
            this.videoUrl = res
            this.selectedVideo = file
            if (inputElement) {
              // eslint-disable-next-line no-param-reassign
              inputElement.value = ''
            }
          }
          this.showModal = true
        })
      }
    },
    async processFileWithoutEmit(file, dataUrl, isVideo = false) {
      if (this.withEndpoin) {
        if (isVideo) {
          const response = await this.uploadVideo(file)
          if (response) {
            return { data: response.data, originalFile: file }
          }
        } else {
          const response = await this.uploadFileToBlobStorage(file)
          if (response) {
            return { data: response.data, originalFile: file }
          }
        }
      } else {
        return { data: file, originalFile: file }
      }
      return null
    },

    uploadFileToBlobStorage(file) {
      // For image files
      return this.uploadMedia(file)
    },
    readAsDataURL(file) {
      return new Promise((resolve, reject) => {
        const fr = new FileReader()
        fr.onerror = reject
        fr.onload = () => {
          resolve(fr.result)
        }
        fr.readAsDataURL(file)
      })
    },
    cropImage() {
      // Use full cropped resolution instead of forcing 120x120
      const final = this.$refs.cropper.getCroppedCanvas()

      const type = this.fileType || 'image/jpeg'
      this.imageUrl = final.toDataURL(type)

      final.toBlob(async blob => {
        this.loadingUpload = true
        if (this.withEndpoin) {
          const response = await this.uploadMedia(blob)
          if (response) {
            this.$emit('finish', response.data, this.index)
            this.$emit('input', this.imageUrl)
          }
        } else {
          this.$emit('finish', blob, this.index)
          this.$emit('input', this.imageUrl)
        }
        this.loadingUpload = false
        this.showModal = false
      }, type)
    },
    async onSubmitVideo() {
      this.loadingUpload = true

      if (this.withEndpoin) {
        const response = await this.uploadVideo(this.selectedVideo)
        if (response) {
          this.$emit('finish', response.data, this.index)
          this.$emit('input', this.videoUrl)
        }
      } else {
        this.$emit('finish', this.selectedVideo, this.index)
        this.$emit('input', this.videoUrl)
      }

      this.loadingUpload = false
      this.showModal = false
    },
    onSubmit(bvModalEvent) {
      // Prevent modal from closing
      bvModalEvent.preventDefault()

      if (this.fileType.includes('image')) {
        this.cropImage()
      }

      if (this.fileType.includes('video')) {
        this.onSubmitVideo()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.m-form-image {
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  cursor: pointer;
  height: 104px;
  text-align: center;
  vertical-align: top;
  width: 104px;
  overflow: hidden;
  &--error {
      border-color: #ea5455;
  }
  &__wrapper {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    text-align: center;
    position: relative;
  }
  &__preview {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  &__labelName {
    opacity: 0;
    position: absolute;
    background: #000000;
    color: #FFF;
    border-radius: 8px;
    padding: 0 0.5rem;
    bottom: 0;
    margin-bottom: 0.2rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 12px;
    width: calc(100% - 6px);
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  &:hover &__labelName {
    opacity: 1;
  }
  input[type="file"] {
    display: none;
  }

  &.is-full {
    width: fit-content !important;
  }

  &.is-dragover {
    border-color: #7367f0 !important;
    background-color: rgba(115, 103, 240, 0.08) !important;
    border-style: dashed !important;
  }
}
</style>
